import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { subscriptionService } from '../../../services/subscription';
import type { ReactivateSubscriptionInput } from './useReactivateSubscription';
import { useTranslation } from '@hooks/useTranslation';

/**
 * Converts a lapsed card-free trial into a paying subscription.
 *
 * On success the organization stops being read-only immediately, so every cached query
 * that was rendering a disabled state has to be refetched — hence the broad invalidation.
 */
export const useActivateSubscriptionAfterTrial = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ReactivateSubscriptionInput) =>
      subscriptionService.activateSubscriptionAfterTrial(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationSubscription'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
      queryClient.invalidateQueries({ queryKey: ['billingSummary'] });
      // Paying also frees a free-trial slot, so the cap the user may be sitting at
      // changes at the same moment.
      queryClient.invalidateQueries({
        queryKey: ['freeTrialOrganizationUsage'],
      });

      toast.success(tActionMessages.subscriptionActiveFullAccessRestored);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.errors?.[0]?.message ||
          error?.message ||
          tActionMessages.couldNotActivateSubscription,
      );
    },
  });
};

export default useActivateSubscriptionAfterTrial;
